import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  useLocation,
  useParams,
  Navigate,
  useNavigate,
} from "react-router-dom";
import axios from "axios";
import {
  IoChevronDown,
  IoChevronUp,
  IoCartOutline,
  IoChevronBack,
  IoChevronForward,
} from "react-icons/io5";
import {
  FaEnvelope,
  FaFileAlt,
  FaLightbulb,
  FaBullseye,
  FaChartLine,
  FaChartBar,
  FaRegCalendarAlt,
  FaBookmark,
  FaCheck,
} from "react-icons/fa";
import { SeoManager } from "../../common/SeoManager";
import { useAuth } from "../../context/AuthContext";

import ReportsHero from "../../common/ReportsHero";
import Breadcrumb from "../../common/Breadcrumb";
import SafeImage from "../../common/SafeImage";
import Reportsummary from "./Reportsummary";
import WhyChooseReport from "./WhyChooseReport";
import ReportDetailsSidebar from "./ReportDetailsSidebar";
import AboutReport from "../ReportPages/AboutReport";
import { REPORT_SECTIONS } from "../ReportPages/reportSections";
import InquiryModal from "../../common/InquiryModal";
import PurchaseModal from "../../common/PurchaseModal";
import "./IndiaDataCenter.scss";
import "../../styles/syndicated-reports.scss";

// Images
import competitiveImg from "../../assets/images/CompetitiveLandscape.png";
import ResearchMethodology5 from "../../assets/images/ResearchMethodology.png";
import ResearchMethodology4 from "../../assets/images/ResearchMethodology1.jpg";
import ResearchMethodology3 from "../../assets/images/ResearchMethodology2.jpg";
import ResearchMethodology1 from "../../assets/images/ResearchMethodology3.jpg";
import ResearchMethodology2 from "../../assets/images/ResearchMethodology4.jpg";
import ipOverviewImg from "../../assets/images/IP-Overview.png";
import coverImg from "../../assets/images/coverImg_V1.png";
import BGcoverImg from "../../assets/images/BGcoverImg.png";

// ─── TOC Accordion ────────────────────────────────────────────────────────────

const TocAccordion = ({ html }) => {
  const [openIndexes, setOpenIndexes] = React.useState([]);

  const toggle = (i) =>
    setOpenIndexes((prev) =>
      prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i],
    );

  // ── Parser 1: Semantic <ol>/<ul> → chapter objects ──────────────────────────
  const parseSemanticChapters = (container) => {
    const chapters = [];
    let chapterNum = 0;

    const walkSubs = (nodes, depth, chapter, parentNum) => {
      let counter = 0;
      nodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        const tag = node.tagName.toLowerCase();
        if (tag === "li") {
          const text =
            node.childNodes[0]?.textContent?.trim() || node.textContent?.trim();
          if (text) {
            counter++;
            const num = `${parentNum}.${counter}`;
            chapter.subs.push({ text, depth, number: num });
            const nested = Array.from(node.children).filter(
              (c) =>
                c.tagName.toLowerCase() === "ol" ||
                c.tagName.toLowerCase() === "ul",
            );
            nested.forEach((nl) =>
              walkSubs(Array.from(nl.children), depth + 1, chapter, num),
            );
          }
        } else {
          walkSubs(Array.from(node.children), depth, chapter, parentNum);
        }
      });
    };

    const walk = (nodes, depth) => {
      nodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        const tag = node.tagName.toLowerCase();
        if (tag === "li") {
          const text =
            node.childNodes[0]?.textContent?.trim() || node.textContent?.trim();
          if (!text) return;
          if (depth === 0) {
            chapterNum += 1;
            const chapter = { num: chapterNum, title: text, subs: [] };
            chapters.push(chapter);
            const nested = Array.from(node.children).filter(
              (c) =>
                c.tagName.toLowerCase() === "ol" ||
                c.tagName.toLowerCase() === "ul",
            );
            nested.forEach((nl) =>
              walkSubs(Array.from(nl.children), 1, chapter, chapterNum),
            );
          }
        } else {
          walk(Array.from(node.children), depth);
        }
      });
    };

    walk(Array.from(container.children), 0);
    return chapters;
  };

  // ── Parser 2: MSO Word <p class="MsoListParagraph..."> → chapter objects ────
  //
  // MSO paragraphs carry depth in two ways:
  //   a) inline style: `mso-list: l0 levelN lfo1`   → level N  (most reliable)
  //   b) inline style: `margin-left: Xpt`            → fallback heuristic
  //
  // Top-level items (level 1) become chapters; deeper items become subs.
  const parseMsoChapters = (container) => {
    // Collect ALL paragraphs that look like list items
    const allP = Array.from(
      container.querySelectorAll(
        'p[class*="MsoList"], p[class*="msolist"], p[class*="List"]',
      ),
    );

    if (allP.length === 0) return [];

    // Helper: extract mso level from inline style string
    const getMsoLevel = (styleStr = "") => {
      // e.g. "mso-list:l0 level2 lfo1"
      const m = styleStr.match(/level(\d+)/i);
      if (m) return parseInt(m[1], 10); // 1-based
      return null;
    };

    // Helper: estimate depth from margin-left (pt values)
    // Typical Word indents: level1≈18pt, level2≈39pt, level3≈61pt, level4≈82pt
    const getMarginLevel = (styleStr = "") => {
      const m = styleStr.match(/margin-left:\s*([\d.]+)pt/i);
      if (!m) return 1;
      const pt = parseFloat(m[1]);
      if (pt < 25) return 1;
      if (pt < 50) return 2;
      if (pt < 72) return 3;
      return 4;
    };

    // Clean up text: remove leading numbering like "1.", "1.1.", "1.1.2." etc.
    const cleanText = (raw = "") => raw.replace(/^[\d.]+\s*/, "").trim();

    // Build flat list of { level, text }
    const flatItems = allP
      .map((p) => {
        const style = p.getAttribute("style") || "";
        const level = getMsoLevel(style) || getMarginLevel(style);

        // Get visible text only (strip inner <span> MSO markers)
        const text = cleanText(p.textContent || "");
        return { level, text };
      })
      .filter((item) => item.text.length > 0);

    if (flatItems.length === 0) return [];

    // Convert flat list → chapter objects
    const chapters = [];
    let currentChapter = null;
    // counters[level] = current count at that depth
    const counters = {};

    flatItems.forEach((item) => {
      const lvl = item.level;
      counters[lvl] = (counters[lvl] || 0) + 1;
      // Reset deeper counters
      Object.keys(counters).forEach((k) => {
        if (parseInt(k) > lvl) counters[k] = 0;
      });

      if (lvl === 1) {
        currentChapter = {
          num: counters[1],
          title: item.text,
          subs: [],
        };
        chapters.push(currentChapter);
      } else if (currentChapter) {
        const numParts = [currentChapter.num];
        for (let d = 2; d <= lvl; d++) {
          numParts.push(counters[d] || 1);
        }
        currentChapter.subs.push({
          text: item.text,
          depth: lvl - 1,
          number: numParts.join("."),
        });
      }
    });

    return chapters;
  };

  // ── Master parser: try semantic first, fall back to MSO ─────────────────────
  const parseChapters = (rawHtml) => {
    if (!rawHtml) return [];
    const container = document.createElement("div");
    container.innerHTML = rawHtml;

    // Try semantic (ol/ul/li) first
    const semantic = parseSemanticChapters(container);
    if (semantic.length > 0) return semantic;

    // Fall back to MSO paragraph parsing
    const mso = parseMsoChapters(container);
    return mso;
  };

  const chapters = parseChapters(html);

  // If still nothing parseable, render raw HTML as before
  if (!chapters.length) {
    return (
      <div
        className="dynamic-content"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }

  return (
    <div className="toc-accordion">
      {chapters.map((ch, i) => {
        const isOpen = openIndexes.includes(i);
        return (
          <div
            key={i}
            className={`toc-chapter-card ${isOpen ? "toc-open" : ""}`}
          >
            <button
              className="toc-chapter-header"
              onClick={() => toggle(i)}
              aria-expanded={isOpen}
            >
              <span className="toc-chapter-num">
                {String(ch.num).padStart(2, "0")}
              </span>
              <span className="toc-chapter-title">{ch.title}</span>
              <span
                className={`toc-chevron ${isOpen ? "toc-chevron-open" : ""}`}
              >
                {/* IoChevronDown from react-icons/io5 */}
                <IoChevronDown size={16} />
              </span>
            </button>
            {isOpen && ch.subs.length > 0 && (
              <div className="toc-chapter-body">
                {ch.subs.map((sub, j) => (
                  <div
                    key={j}
                    className="toc-sub-item"
                    style={{ paddingLeft: `${(sub.depth - 1) * 20}px` }}
                  >
                    <span className="toc-sub-num">{sub.number}</span>
                    <span className="toc-sub-text">{sub.text}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
// ──────────────────────────────────────────────────────────────────────────────

const MethodologyCarousel = ({ images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  const minSwipeDistance = 50;

  const nextSlide = () => {
    if (currentIndex < images.length - 1 && !isAnimating) {
      setIsAnimating(true);
      setCurrentIndex((prev) => prev + 1);
      setTimeout(() => setIsAnimating(false), 400);
    }
  };

  const prevSlide = () => {
    if (currentIndex > 0 && !isAnimating) {
      setIsAnimating(true);
      setCurrentIndex((prev) => prev - 1);
      setTimeout(() => setIsAnimating(false), 400);
    }
  };

  // 👇 Swipe handlers
  const onTouchStart = (e) => {
    touchEndX.current = null;
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const onTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const onTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;

    const distance = touchStartX.current - touchEndX.current;

    if (distance > minSwipeDistance) nextSlide();
    if (distance < -minSwipeDistance) prevSlide();
  };

  if (!images || images.length === 0) return null;

  return (
    <div className="methodology-carousel-container mb-2 position-relative">
      <div className="carousel shadow-sm rounded-4 border bg-white overflow-hidden p-2">
        <div
          className="methodology-carousel-img-container position-relative"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* ✅ Track */}
          <div
            className="carousel-track"
            style={{
              transform: `translate3d(-${currentIndex * 100}%, 0, 0)`,
            }}
          >
            {images.map((img, index) => (
              <div key={index} className="carousel-slide">
                <img src={img} alt={`Methodology ${index + 1}`} />
              </div>
            ))}
          </div>

          {/* ✅ Prev Button */}
          {currentIndex > 0 && (
            <button
              className="carousel-btn left"
              onClick={prevSlide}
              style={{ touchAction: "manipulation" }}
            >
              <IoChevronBack size={20} />
            </button>
          )}

          {/* ✅ Next Button */}
          {currentIndex < images.length - 1 && (
            <button className="carousel-btn right" onClick={nextSlide}>
              <IoChevronForward size={20} />
            </button>
          )}
        </div>

        {/* ✅ Indicators */}
        <div className="d-flex justify-content-center gap-2 mt-3">
          {images.map((_, index) => (
            <div
              key={index}
              className="indicator-dot"
              style={{
                width: index === currentIndex ? "12px" : "8px",
                height: index === currentIndex ? "12px" : "8px",
                opacity: index === currentIndex ? 1 : 0.3,
                backgroundColor: index === currentIndex ? "#053b84" : "#6c757d",
              }}
              onClick={() => !isAnimating && setCurrentIndex(index)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const InsightsCarousel = ({ cards }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const nextSlide = React.useCallback(() => {
    setCurrentIndex((prev) => (prev === cards.length - 1 ? 0 : prev + 1));
  }, [cards.length]);

  const prevSlide = React.useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? cards.length - 1 : prev - 1));
  }, [cards.length]);

  useEffect(() => {
    if (!cards || cards.length <= 1 || isPaused) return;

    // Enable auto-scroll ONLY for mobile and tablet (width < 1024px)
    if (window.innerWidth >= 1024) return;

    const interval = setInterval(() => {
      nextSlide();
    }, 4000);

    return () => clearInterval(interval);
  }, [cards, isPaused, nextSlide]);

  if (!cards || cards.length === 0) return null;

  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) nextSlide();
    if (isRightSwipe) prevSlide();
  };

  return (
    <div
      className="insights-carousel-container overflow-hidden position-relative pb-4"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={(e) => {
        setIsPaused(true);
        onTouchStart(e);
      }}
      onTouchEnd={() => {
        setIsPaused(false);
        onTouchEnd();
      }}
    >
      {/* Navigation Arrows */}
      {cards.length > 1 && (
        <>
          <button
            className="btn btn-link position-absolute start-0 text-dark z-2 p-0 ms-1 d-flex align-items-center justify-content-center"
            onClick={prevSlide}
            style={{
              top: "50%",
              transform: "translateY(-50%)",
              backgroundColor: "rgba(255,255,255,0.7)",
              borderRadius: "50%",
              width: "32px",
              height: "32px",
            }}
          >
            <IoChevronBack size={18} />
          </button>
          <button
            className="btn btn-link position-absolute end-0 text-dark z-2 p-0 me-1 d-flex align-items-center justify-content-center"
            onClick={nextSlide}
            style={{
              top: "50%",
              transform: "translateY(-50%)",
              backgroundColor: "rgba(255,255,255,0.7)",
              borderRadius: "50%",
              width: "32px",
              height: "32px",
            }}
          >
            <IoChevronForward size={18} />
          </button>
        </>
      )}

      <div
        className="d-flex transition-transform duration-300"
        style={{
          transform: `translate3d(-${currentIndex * 100}%, 0, 0)`,
          transition: "transform 0.3s ease-out",
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {cards.map((card, index) => (
          <div key={index} className="w-100 flex-shrink-0 px-2 py-1">
            <div className="w-100 p-3 bg-white rounded shadow-sm border d-flex flex-column h-100">
              <div className="d-flex align-items-center gap-2 mb-3">
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle"
                  style={{
                    width: "32px",
                    height: "32px",
                    backgroundColor: card.bgColor,
                  }}
                >
                  <card.icon size={18} style={{ color: card.color }} />
                </div>
                <h6 className="fw-bold mb-0 text-dark text-nowrap keyworsdTitle">
                  {card.title}
                </h6>
              </div>
              <ul className="list-unstyled d-flex flex-column gap-1 mb-0">
                {card.items.map((item, i) => (
                  <li key={i} className="d-flex align-items-start gap-2">
                    <span
                      className="flex-shrink-0 text-dark fw-bold"
                      style={{ marginTop: "-2px", fontSize: "18px" }}
                    >
                      •
                    </span>
                    <span className="text-secondary keyworsdText">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      <div className="d-flex justify-content-center gap-2 position-absolute bottom-0 w-100 pb-1">
        {cards.map((_, index) => (
          <div
            key={index}
            className={`rounded-circle cursor-pointer transition-all duration-300 ${index === currentIndex ? "bg-primary" : "bg-secondary"}`}
            style={{
              width: index === currentIndex ? "10px" : "8px",
              height: index === currentIndex ? "10px" : "8px",
              opacity: index === currentIndex ? 1 : 0.3,
              backgroundColor: index === currentIndex ? "#053b84" : "#6c757d",
              cursor: "pointer",
            }}
            onClick={() => setCurrentIndex(index)}
          />
        ))}
      </div>
    </div>
  );
};

const IndiaDataCenter = () => {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { endUser, savedReports, saveSearchReport } = useAuth();

  const [report, setReport] = useState(location.state?.report || null);
  const [loading, setLoading] = useState(!location.state?.report);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState(REPORT_SECTIONS.ABOUT);
  const [openFaqIndex, setOpenFaqIndex] = useState(-1);
  const [showModal, setShowModal] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [modalType, setModalType] = useState("sample_download");

  const isUserLoggedIn = Boolean(
    (endUser && (endUser.email || endUser.id)) ||
    (() => {
      try {
        const u = JSON.parse(localStorage.getItem("rw_end_user") || "null");
        return u && (u.email || u.id);
      } catch (e) {
        return false;
      }
    })()
  );

  const onBuyNow = () => {
    const targetUrl = `/purchase/${report?.slug || report?.id || slug}`;
    if (!isUserLoggedIn) {
      navigate(`/signin?redirect=${encodeURIComponent(targetUrl)}`);
      return;
    }
    navigate(targetUrl, { state: { report } });
  };

  const handleOpenModal = (type) => {
    if (type === "customization" || type === "sample_download") {
      if (!isUserLoggedIn) {
        navigate(`/signin?redirect=${encodeURIComponent(`/reports/${slug}`)}`);
        return;
      }
    }
    setModalType(type);
    setShowModal(true);
  };

  const handleTabClick = (sectionKey) => {
    const sectionIds = {
      [REPORT_SECTIONS.ABOUT]: "market-overview",
      [REPORT_SECTIONS.DYNAMICS]: "market-dynamics",
      [REPORT_SECTIONS.SEGMENTATION]: "segmentation-analysis",
      [REPORT_SECTIONS.DEVELOPMENTS]: "recent-developments",
      [REPORT_SECTIONS.COMPETITIVE]: "competitive-landscape",
      [REPORT_SECTIONS.SCOPE]: "market-scope",
      [REPORT_SECTIONS.IP_OVERVIEW]: "ip-overview",
      [REPORT_SECTIONS.FAQ]: "faq",
      [REPORT_SECTIONS.TOC]: "table-of-contents",
      [REPORT_SECTIONS.METHODOLOGY]: "research-methodology",
    };

    let id = sectionIds[sectionKey];
    if (!id && sectionKey.startsWith("seg-")) {
      id = sectionKey; // Dynamic segment ID
    }
    const element = document.getElementById(id);
    if (element) {
      const offset = 140; // Adjust for sticky header + tabs
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const sections = [
      "market-overview",
      "market-dynamics",
      "segmentation-analysis",
      "ip-overview",
      "recent-developments",
      "competitive-landscape",
      "market-scope",
      "table-of-contents",
      "research-methodology",
      "faq",
    ];

    const activeSections = new Set();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            activeSections.add(entry.target.id);
          } else {
            activeSections.delete(entry.target.id);
          }
        });

        // Get all intersecting sections and sort them by their actual vertical position in the DOM
        // to find the one closest to the top of the viewport (near the sticky header offset)
        let closestSection = null;

        sections.forEach((id) => {
          if (activeSections.has(id)) {
            const el = document.getElementById(id);
            if (el) {
              const rect = el.getBoundingClientRect();
              // We want the element whose top is closest to the 140px offset,
              // but if an element is very tall and its top is way above (rect.top < 0),
              // it is still the main reading section.
              // So we take the one that occupies the top part of the visible window.
              if (rect.top < window.innerHeight * 0.5) {
                closestSection = id; // Pick the latest one that is above the middle of the screen
              }
            }
          }
        });

        const firstIntersecting =
          closestSection || sections.find((id) => activeSections.has(id));

        if (firstIntersecting) {
          if (firstIntersecting === "market-overview")
            setActiveSection(REPORT_SECTIONS.ABOUT);
          else if (firstIntersecting === "market-dynamics")
            setActiveSection(REPORT_SECTIONS.DYNAMICS);
          else if (firstIntersecting === "segmentation-analysis")
            setActiveSection(REPORT_SECTIONS.SEGMENTATION);
          else if (firstIntersecting === "ip-overview")
            setActiveSection(REPORT_SECTIONS.IP_OVERVIEW);
          else if (firstIntersecting === "recent-developments")
            setActiveSection(REPORT_SECTIONS.DEVELOPMENTS);
          else if (firstIntersecting === "competitive-landscape")
            setActiveSection(REPORT_SECTIONS.COMPETITIVE);
          else if (firstIntersecting === "market-scope")
            setActiveSection(REPORT_SECTIONS.SCOPE);
          else if (firstIntersecting === "table-of-contents")
            setActiveSection(REPORT_SECTIONS.TOC);
          else if (firstIntersecting === "research-methodology")
            setActiveSection(REPORT_SECTIONS.METHODOLOGY);
          else if (firstIntersecting === "faq")
            setActiveSection(REPORT_SECTIONS.FAQ);
        }
      },
      {
        root: null,
        rootMargin: "-140px 0px -40% 0px",
        threshold: 0,
      },
    );

    sections.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    const handleScroll = () => {
      // If user has scrolled within 10px of the absolute bottom
      if (
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 10
      ) {
        setActiveSection(REPORT_SECTIONS.METHODOLOGY);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", handleScroll);
    };
  }, [report]);

  // Helper to process content: extract images and return cleaned HTML + images array
  const processContent = (htmlContent) => {
    if (!htmlContent) return { images: [], cleanedHtml: "" };
    const images = [];
    const imgTagRegex = /<img[^>]+src="([^">]+)"[^>]*>/g;
    let match;
    while ((match = imgTagRegex.exec(htmlContent)) !== null) {
      images.push(match[1]);
    }
    const cleanedHtml = htmlContent.replace(imgTagRegex, "");
    return { images, cleanedHtml };
  };

  const renderSegmentationContent = (seg, index) => {
    if (!seg || !seg.content) return null;

    const { images, cleanedHtml } = processContent(seg.content);

    return (
      <div
        id={`seg-${index}`}
        key={index}
        className={`mt-2 border-light-subtle segmentation-content ${
          index === 0 ? "pt-1" : "pt-2"
        }`}
      >
        <h5 className="fw-bold mb-3 keyworsdTitle" style={{ color: "#053b84" }}>
          {seg.title || `Segment ${index + 1}`}
        </h5>
        {/* Images Grid */}
        {images.length > 0 && (
          <div className="row mb-4">
            {images.map((src, idx) => (
              <div
                key={idx}
                className={images.length === 1 ? "col-12" : "col-md-6"}
              >
                <div className="d-flex align-items-center justify-content-start w-100 h-100">
                  <SafeImage
                    src={src}
                    alt={`${seg.title} Graph ${idx + 1}`}
                    className="img-fluid w-100"
                    style={{ maxHeight: "400px", objectFit: "contain" }}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
        {/* Text Content */}
        <div
          className="dynamic-content"
          style={{ all: "revert", lineHeight: "1.4", color: "inherit" }}
          dangerouslySetInnerHTML={{ __html: cleanedHtml }}
        />
      </div>
    );
  };

  useEffect(() => {
    const fetchReport = async () => {
      try {
        if (!report) setLoading(true);
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/reports/slug/${slug}`,
        );
        const r = res.data;

        const parseJson = (data, fallback) => {
          if (!data) return fallback;
          if (typeof data === "object") return data;
          try {
            return JSON.parse(data);
          } catch (e) {
            console.error("JSON Parse Error:", e);
            return fallback;
          }
        };

        // 1. Data Parsing
        // let segmentationAnalysis = parseJson(r.segmentation_analysis, null);
        let marketDynamics = parseJson(r.market_dynamics, {});
        let faqs = parseJson(r.faqs, []);
        let recentDevelopments = parseJson(r.recent_developments, []);
        let breadcrumbs = parseJson(r.breadcrumbs, null);

        // 2. Transform Image URLs in Segmentation Analysis
        let segmentations = [];
        const rawSeg = parseJson(r.segmentation_analysis, []);

        if (Array.isArray(rawSeg)) {
          segmentations = rawSeg;
        } else if (typeof rawSeg === "object" && rawSeg !== null) {
          if (rawSeg.technology)
            segmentations.push({
              title: "By Technology",
              content: rawSeg.technology,
            });
          if (rawSeg.application)
            segmentations.push({
              title: "By Application",
              content: rawSeg.application,
            });
          if (rawSeg.region)
            segmentations.push({ title: "By Region", content: rawSeg.region });
        }

        segmentations = segmentations.map((seg) => ({
          ...seg,
          content: seg.content || "",
        }));

        let ipOverview = r.ip_overview;

        let description = r.description;

        // Find parent segment for fastest growing
        let fastestGrowingSegment =
          r.fastest_growing_segment || r.FastestGrowingSegment || "";
        let fastestGrowingParent = "";
        if (fastestGrowingSegment && segmentations.length > 0) {
          const match = segmentations.find(
            (seg) =>
              seg.content &&
              seg.content
                .toLowerCase()
                .includes(fastestGrowingSegment.toLowerCase()),
          );
          if (match) {
            fastestGrowingParent = match.title;
          }
        }

        // 3. Transform Cover Image
        let coverImageUrl = r.coverImageUrl || r.cover_image_url;

        // 4. Construct Breadcrumbs if missing
        if (!breadcrumbs) {
          const mainInd = r.industry ? r.industry.split(",")[0].trim() : "";
          const subInd =
            r.sub_category || r.subCategory
              ? (r.sub_category || r.subCategory).split(",")[0].trim()
              : "";

          breadcrumbs = [
            { label: "Home", link: "/" },
            { label: "Reports", link: "/reports" },
          ];

          if (mainInd) {
            breadcrumbs.push({
              label: mainInd,
              link: `/reports?industry=${encodeURIComponent(mainInd)}`,
            });
          }
          if (subInd) {
            breadcrumbs.push({
              label: subInd,
              link: `/reports?subcategory=${encodeURIComponent(subInd)}`,
            });
          }
          breadcrumbs.push({ label: r.title || "Report Details" });
        }

        // 5. Construct Final Report Object
        const finalReport = {
          id: r.id,
          title: r.title,
          subtitle: r.subtitle,
          Industry: r.industry,
          RegionsCovered: r.region,
          MajorPlayers: r.major_players,
          Author: r.author,
          PublicationDate: r.publication_date
            ? new Date(r.publication_date).toLocaleString("default", {
                month: "long",
                year: "numeric",
              })
            : "January 2025",
          ProductCode: r.product_code,
          StudyPeriod: r.forecast_period || "2024-2033",
          Description: description,
          Price: r.price,
          price: r.price, // Mapping for PurchaseModal
          SampleReportUrl: r.sample_report_url,
          FullReportUrl: r.full_report_url,
          MarketSize: r.market_size || "USD XX Billion",
          MarketSizeForecast:
            r.market_size_forecast || r.market_size || "USD XX Billion",
          CAGR: r.cagr || "X.X%",
          ipOverview: ipOverview,
          ReportYear: r.report_year,
          Pages: r.pages || r.Pages,
          SubCategory: r.sub_category || r.subCategory || "",
          createdAt: r.created_at || r.createdAt,
          updatedAt: r.updated_at || r.updatedAt,
          FastestGrowingSegment: fastestGrowingSegment,
          FastestGrowingParent: fastestGrowingParent,
          LargestMarket: r.largest_market,

          // Processed Fields
          MarketDynamics: marketDynamics,
          Faqs: faqs,
          RecentDevelopments: recentDevelopments,
          SegmentationAnalysis: segmentations,
          breadcrumbs: breadcrumbs,
          coverImageUrl: coverImageUrl,
          pricingOptions: parseJson(r.pricing_options, null), // Add this!

          // HTML Fields
          TableOfContents: r.table_of_contents || "",
          Methodology: r.methodology || "",
          CompetitorAnalysis: r.competitor_analysis || "",
          slug: r.slug,
          status: r.status || "published",
        };

        setReport(finalReport);

        // SEO Redirect: If accessed via ID (/reports/7), redirect to Slug (/reports/battery-slug)
        if (!isNaN(slug) && r.slug) {
          window.history.replaceState(null, "", `/reports/${r.slug}`);
        }
      } catch (err) {
        console.error("Failed to fetch report from API", err);
        if (!report && !location.state?.report) {
          setError("Failed to load report");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const isSaved = useMemo(() => {
    if (!report || !savedReports) return false;
    return savedReports.some(
      (item) =>
        item.title === report.title ||
        (item.slug && item.slug === report.slug) ||
        (item.id && String(item.id) === String(report.id)),
    );
  }, [report, savedReports]);

  const [inquiryStatus, setInquiryStatus] = useState({
    sampleRequested: false,
    customizationRequested: false,
  });

  useEffect(() => {
    let isMounted = true;

    const checkStatus = async () => {
      if (!report?.id) return;

      const activeEmail = (
        endUser?.email ||
        (() => {
          try {
            return JSON.parse(localStorage.getItem("rw_end_user") || "null")?.email;
          } catch (e) {
            return null;
          }
        })() ||
        ""
      )
        .toLowerCase()
        .trim();

      // 1. Check local storage cache (fast, resilient)
      let localSample = false;
      let localCustom = false;
      try {
        const recent = JSON.parse(localStorage.getItem("rw_recent_inquiries") || "[]");
        const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
        recent.forEach((item) => {
          const matchesReport =
            (item.reportId && String(item.reportId) === String(report.id)) ||
            (item.reportSlug && item.reportSlug === report.slug) ||
            (item.reportTitle && item.reportTitle === report.title);

          const matchesEmail = !activeEmail || !item.email || item.email === activeEmail;

          if (matchesReport && matchesEmail && item.timestamp >= sevenDaysAgo) {
            if (item.type === "sample" || item.type === "sample_download") localSample = true;
            if (item.type === "customization" || item.type === "custom_research") localCustom = true;
          }
        });
      } catch (e) {}

      if (isMounted) {
        setInquiryStatus((prev) => ({
          sampleRequested: prev.sampleRequested || localSample,
          customizationRequested: prev.customizationRequested || localCustom,
        }));
      }

      // 2. Query backend API if user email is present
      if (activeEmail && report?.id) {
        try {
          const apiBase = process.env.REACT_APP_API_BASE_URL || "/api";
          const res = await axios.get(
            `${apiBase}/reports/${encodeURIComponent(report.id)}/inquiry-status?email=${encodeURIComponent(activeEmail)}`
          );
          if (isMounted && res.data?.success) {
            setInquiryStatus({
              sampleRequested: Boolean(res.data.sampleRequested || localSample),
              customizationRequested: Boolean(res.data.customizationRequested || localCustom),
            });
          }
        } catch (e) {
          // ignore network failure
        }
      }
    };

    checkStatus();

    const handleInquiryUpdated = () => {
      checkStatus();
    };

    window.addEventListener("rwire_inquiry_submitted", handleInquiryUpdated);
    window.addEventListener("rwire_customized_reports_updated", handleInquiryUpdated);

    return () => {
      isMounted = false;
      window.removeEventListener("rwire_inquiry_submitted", handleInquiryUpdated);
      window.removeEventListener("rwire_customized_reports_updated", handleInquiryUpdated);
    };
  }, [report, endUser]);

  const handleSaveReport = () => {
    if (!isUserLoggedIn) {
      navigate(`/signin?redirect=${encodeURIComponent(`/reports/${slug}`)}`);
      return;
    }
    if (report && saveSearchReport) {
      saveSearchReport("", report);
    }
  };

  const isLatest = useMemo(() => {
    if (!report?.createdAt && !report?.updatedAt) return false;
    const reportDate = new Date(report.updatedAt || report.createdAt);
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    return reportDate >= oneWeekAgo;
  }, [report]);

  if (loading) return <ReportSkeleton />;
  if (error && !report) {
    return <Navigate to="/reports" replace />;
  }

  if (!report) {
    return <Navigate to="/reports" replace />;
  }

  // ENFORCE PREVIEW MODE: If report is explicitly draft, ONLY allow if preview=true is in URL
  const isPreview = location.search.includes("preview=true");
  if (report.status?.toLowerCase() === "draft" && !isPreview) {
    return <Navigate to="/reports" replace />;
  }

  const reportSchema = report
    ? {
        "@context": "https://schema.org",
        "@type": "Product",
        name: report.title,
        description: report.subtitle
          ? report.subtitle.replace(/<[^>]*>?/gm, "")
          : report.Description
            ? report.Description.replace(/<[^>]*>?/gm, "").substring(0, 160)
            : "",
        brand: {
          "@type": "Brand",
          name: "Researchwire Market",
        },
        sku: report.ProductCode,
        offers: {
          "@type": "Offer",
          priceCurrency: "USD",
          price: report.Price || 0,
          availability: "https://schema.org/InStock",
          url: `https://rwiremarket.com/reports/${slug}`,
        },
      }
    : null;
  const cleanDescription = report.subtitle
    ? report.subtitle.replace(/<[^>]*>?/gm, "").trim()
    : report.Description
      ? report.Description.replace(/<[^>]*>?/gm, "")
          .trim()
          .substring(0, 160)
      : "";
  const seoDescription =
    report.meta_description ||
    `Comprehensive market research report on ${report.title}. ${cleanDescription}`.substring(
      0,
      160,
    );
  const seoTitle =
    report.meta_title ||
    (report.title.includes("Market")
      ? `${report.title} - Size, Share, Trends & Forecast`
      : `${report.title} Market - Size, Share, Trends & Forecast`);
  const actualSlug = report.slug || slug;

  return (
    <>
      <SeoManager
        absoluteTitle={seoTitle}
        description={seoDescription}
        keywords={report.keywords || ""}
        ogTitle={seoTitle}
        ogDescription={seoDescription}
        ogImage={
          report.coverImageUrl ||
          "https://www.rwmarketintelligence.com/Rwirelogo.png"
        }
        ogUrl={`https://www.rwmarketintelligence.com/reports/${actualSlug}`}
        canonicalUrl={`https://www.rwmarketintelligence.com/reports/${actualSlug}`}
        schema={reportSchema}
      />
      {report.status === "draft" && (
        <div
          className="bg-warning text-dark px-3 py-2 text-center fw-bold w-100"
          style={{ zIndex: 9999, position: "relative" }}
        >
          ⚠️ PREVIEW MODE: This report is currently a Draft and is NOT visible
          to the public.
        </div>
      )}
      <main className="bg-body-tertiary report-view-page">
        <div className="bgcolor mb-4">
          <ReportsHero
            title={report.title}
            absoluteBackground={BGcoverImg}
            tooltip={
              report.subtitle ? report.subtitle.replace(/<[^>]*>?/gm, "") : ""
            }
            description={
              report.subtitle ? (
                <div
                  style={{ lineHeight: "1.6", color: "inherit" }}
                  dangerouslySetInnerHTML={{
                    __html: report.subtitle,
                  }}
                />
              ) : (
                "Syndicated market research reports are comprehensive studies that provide valuable insights into various industries, markets, and emerging trends."
              )
            }
            badges={[
              ...(isLatest
                ? [{ text: "New", color: "#F59E0B", textColor: "#FFFFFF" }]
                : []),
              ...(report.Industry
                ? report.Industry.split(",").map((name) => ({
                    text: name.trim(),
                    color: "#4B5563",
                    textColor: "#FFFFFF",
                  }))
                : []),
            ]}
            stats={[
              {
                label: "Market Size",
                value:
                  report.MarketSizeForecast ||
                  report.MarketSize ||
                  "USD XX Billion",
                subtext: `by ${(() => {
                  const period = report.StudyPeriod || "";
                  const match = period.match(/\d{4}$/);
                  return match ? match[0] : "2030";
                })()}`,
                icon: FaChartBar,
              },
              {
                label: "CAGR",
                value: report.CAGR,
                subtext: "Forecast Period",
                icon: (
                  <svg viewBox="0 0 46 46" width="20" height="20">
                    <rect
                      x="8"
                      y="32"
                      width="6"
                      height="8"
                      rx="1.5"
                      fill="#053b84"
                    />
                    <rect
                      x="18"
                      y="24"
                      width="6"
                      height="16"
                      rx="1.5"
                      fill="#053b84"
                    />
                    <rect
                      x="28"
                      y="16"
                      width="6"
                      height="24"
                      rx="1.5"
                      fill="#053b84"
                    />
                    <polyline
                      points="8,37 18,29 28,21 37,12"
                      fill="none"
                      stroke="#053b84"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                    <polyline
                      points="32,12 37,12 37,17"
                      fill="none"
                      stroke="#053b84"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ),
              },
              {
                label: "Published",
                value: report.PublicationDate,
                subtext: "Latest Edition",
                icon: FaRegCalendarAlt,
              },
            ]}
            actions={true}
            onDownloadSample={() => handleOpenModal("sample_download")}
            onRequestCustomization={() => handleOpenModal("customization")}
            onBuyNow={onBuyNow}
            onAddReport={handleSaveReport}
            isSaved={isSaved}
            isSampleRequested={inquiryStatus.sampleRequested}
            isCustomizationRequested={inquiryStatus.customizationRequested}
            customImageSection={
              <div className="cover-book-container position-relative">
                {/* Book Wrapper - Now contains cards to anchor them */}
                <div className="cover-book-wrapper position-relative d-inline-block">
                  {/* Top-left: Business Intelligence Report */}
                  <div className="floating-card insights d-flex">
                    <div className="card-content">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <svg viewBox="0 0 46 46" width="38" height="38">
                          <rect width="46" height="46" rx="10" fill="#eaf2fd" />
                          <rect
                            x="10"
                            y="30"
                            width="7"
                            height="10"
                            rx="2"
                            fill="#3b82f6"
                          />
                          <rect
                            x="21"
                            y="22"
                            width="7"
                            height="18"
                            rx="2"
                            fill="#3b82f6"
                          />
                          <rect
                            x="32"
                            y="15"
                            width="7"
                            height="25"
                            rx="2"
                            fill="#1d4ed8"
                          />
                          <polyline
                            points="10,35 21,27 32,22"
                            fill="none"
                            stroke="#60a5fa"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                        <h6>Business Intelligence Report</h6>
                      </div>
                      <p>Actionable insights to support business decisions.</p>
                    </div>
                  </div>

                  {/* Bottom-left: Competitive Landscape */}
                  <div className="floating-card coverage d-flex">
                    <div className="card-content">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <svg viewBox="0 0 46 46" width="38" height="38">
                          <rect width="46" height="46" rx="10" fill="#eaf2fd" />
                          <circle
                            cx="23"
                            cy="23"
                            r="14"
                            fill="none"
                            stroke="#3b82f6"
                            strokeWidth="2"
                          />
                          <ellipse
                            cx="23"
                            cy="23"
                            rx="6"
                            ry="14"
                            fill="none"
                            stroke="#3b82f6"
                            strokeWidth="1.5"
                          />
                          <line
                            x1="9"
                            y1="23"
                            x2="37"
                            y2="23"
                            stroke="#3b82f6"
                            strokeWidth="1.5"
                          />
                          <line
                            x1="10"
                            y1="17"
                            x2="36"
                            y2="17"
                            stroke="#3b82f6"
                            strokeWidth="1"
                          />
                          <line
                            x1="10"
                            y1="29"
                            x2="36"
                            y2="29"
                            stroke="#3b82f6"
                            strokeWidth="1"
                          />
                        </svg>
                        <h6>Competitive Landscape</h6>
                      </div>
                      <p>Key trends, players, and growth outlook</p>
                    </div>
                  </div>

                  {/* Right: Market Outlook */}
                  <div className="floating-card actionable d-flex">
                    <div className="card-content">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <svg viewBox="0 0 46 46" width="35" height="35">
                          <rect width="46" height="46" rx="10" fill="#eaf2fd" />
                          <circle cx="23" cy="23" r="14" fill="#e0eafd" />
                          <path
                            d="M23,23 L23,9 A14,14 0 0,1 37,23 Z"
                            fill="#1d4ed8"
                          />
                          <path
                            d="M23,23 L37,23 A14,14 0 0,1 17,36 Z"
                            fill="#3b82f6"
                          />
                          <path
                            d="M23,23 L17,36 A14,14 0 0,1 9,21 Z"
                            fill="#93c5fd"
                          />
                          <path
                            d="M23,23 L9,21 A14,14 0 0,1 23,9 Z"
                            fill="#60a5fa"
                          />
                        </svg>
                        <h6>Market Outlook</h6>
                      </div>
                      <p>Forecast, demand drivers, and future opportunities.</p>
                    </div>
                  </div>

                  <img
                    src={coverImg}
                    alt="Report Cover"
                    className="cover-book-img img-fluid"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="cover-book-overlay position-absolute d-flex flex-column justify-content-center align-items-start px-3">
                    <p className="cover-book-title mb-1 fw-bold">
                      {report.title}
                    </p>
                  </div>
                </div>
              </div>
            }
          />

          <InquiryModal
            show={showModal}
            onClose={() => setShowModal(false)}
            report={report}
            type={modalType}
          />

          <PurchaseModal
            show={showPurchaseModal}
            onClose={() => setShowPurchaseModal(false)}
            report={report}
          />

          <div className="container-fluid px-3">
            <div className="mb-4">
              <Breadcrumb
                items={[
                  { label: "Home", link: "/" },
                  { label: "Reports", link: "/reports" },
                  ...(report.Industry
                    ? [
                        {
                          label: report.Industry.split(",")[0].trim(),
                          link: `/reports?industry=${encodeURIComponent(report.Industry.split(",")[0].trim())}`,
                        },
                      ]
                    : []),
                  ...(report.SubCategory
                    ? [
                        {
                          label: report.SubCategory.split(",")[0].trim(),
                          link: `/reports?subcategory=${encodeURIComponent(report.SubCategory.split(",")[0].trim())}`,
                        },
                      ]
                    : []),
                  { label: report.title || "Report" },
                ]}
              />
            </div>

            <div className="row px-3 pb-4">
              <div className="col-lg-2 col-md-2 p-0 mb-4 mb-md-0">
                <aside className="sidebar-layout-wrapper">
                  <Reportsummary
                    activeSection={activeSection}
                    onSectionChange={handleTabClick}
                    segmentations={report.SegmentationAnalysis}
                  />
                  <div className="d-none d-md-block">
                    <WhyChooseReport />
                  </div>
                  <div className="d-md-none mt-4">
                    <ReportDetailsSidebar
                      report={report}
                      onBuyNow={onBuyNow}
                      onDownloadSample={() =>
                        handleOpenModal("sample_download")
                      }
                      onRequestCustomization={() =>
                        handleOpenModal("customization")
                      }
                      onAddReport={handleSaveReport}
                      isSaved={isSaved}
                      isSampleRequested={inquiryStatus.sampleRequested}
                      isCustomizationRequested={inquiryStatus.customizationRequested}
                    />
                  </div>
                </aside>
              </div>

              <div className="col-lg-8 col-md-8 px-md-3 px-0">
                {/* Tab Navigation */}
                <div
                  className="d-flex gap-1 text-nowrap sticky-top bg-body-tertiary scrollbar-hide"
                  style={{
                    top: "80px",
                    zIndex: 1020,
                    paddingBottom: "10px",
                    overflowX: "auto",
                  }}
                >
                  {[
                    {
                      label: "Overview",
                      key: REPORT_SECTIONS.ABOUT,
                      group: [
                        REPORT_SECTIONS.ABOUT,
                        REPORT_SECTIONS.DYNAMICS,
                        REPORT_SECTIONS.SEGMENTATION,
                        REPORT_SECTIONS.IP_OVERVIEW,
                        REPORT_SECTIONS.DEVELOPMENTS,
                        REPORT_SECTIONS.COMPETITIVE,
                      ],
                    },
                    {
                      label: "Market Report scope",
                      key: REPORT_SECTIONS.SCOPE,
                      group: [REPORT_SECTIONS.SCOPE],
                    },
                    {
                      label: "Table of Content",
                      key: REPORT_SECTIONS.TOC,
                      group: [REPORT_SECTIONS.TOC],
                    },
                    {
                      label: "Methodology",
                      key: REPORT_SECTIONS.METHODOLOGY,
                      group: [REPORT_SECTIONS.METHODOLOGY],
                    },
                    {
                      label: "FAQ",
                      key: REPORT_SECTIONS.FAQ,
                      group: [REPORT_SECTIONS.FAQ],
                    },
                  ].map((tab) => {
                    const isActive = tab.group.includes(activeSection);
                    return (
                      <button
                        key={tab.label}
                        className={`btn px-2 py-2 fw-medium ${isActive ? "text-white active-tab" : "text-secondary inactive-tab"} report-tab-btn`}
                        style={{
                          background: isActive
                            ? "linear-gradient(0deg, #012970, #024ed6)"
                            : "#E5E7EB",
                          border: "none",
                          borderRadius: "6px 6px 0 0",
                          flex: 1,
                        }}
                        onClick={() => handleTabClick(tab.key)}
                      >
                        {tab.label}
                      </button>
                    );
                  })}
                </div>

                <div className="report-content-wrapper">
                  {/* Overview Section */}
                  <div id="market-overview" className="mb-4">
                    <AboutReport
                      report={{
                        ...report,
                        description: report.description
                          ?.replace(/<p[^>]*>\s*\[Type here\]\s*<\/p>/gi, "")
                          ?.replace(/\[Type here\]/gi, ""),
                      }}
                    />

                    <div className="mt-4">
                      <div
                        className="card border-0 shadow-sm mb-4"
                        id="market-dynamics"
                      >
                        <div className="hero-gradient-bg p-3 bg-white shadow-sm rounded-2">
                          <h4 className="fw-bold mb-0 reports-hero-title">
                            Key Market Insights
                          </h4>
                        </div>
                        {(() => {
                          const insightCards = [
                            {
                              title: "Key Market Drivers",
                              icon: FaLightbulb,
                              color: "#ca8a04",
                              bgColor: "#fcf6c3",
                              items: report.MarketDynamics?.drivers || [],
                            },
                            {
                              title: "Key Market Challenges",
                              icon: FaBullseye,
                              color: "#dc2626",
                              bgColor: "#fef2f2",
                              items: report.MarketDynamics?.restraints || [],
                            },
                            {
                              title: "Key Market Trends",
                              icon: FaChartLine,
                              color: "#053b84",
                              bgColor: "#eff6ff",
                              items: report.MarketDynamics?.trends || [],
                            },
                          ].filter(
                            (card) => card.items && card.items.length > 0,
                          );

                          return (
                            <div className="p-2">
                              {/* Desktop View: 3-Column Grid */}
                              <div
                                className="d-none d-lg-grid"
                                style={{
                                  gridTemplateColumns: "repeat(3, 1fr)",
                                  gap: "15px",
                                }}
                              >
                                {insightCards.map((card, index) => (
                                  <div
                                    key={index}
                                    className="p-3 bg-white rounded shadow-sm border d-flex flex-column h-100"
                                  >
                                    <div className="d-flex align-items-center gap-2 mb-3">
                                      <div
                                        className="d-flex align-items-center justify-content-center rounded-circle"
                                        style={{
                                          width: "32px",
                                          height: "32px",
                                          backgroundColor: card.bgColor,
                                        }}
                                      >
                                        <card.icon
                                          size={18}
                                          style={{ color: card.color }}
                                        />
                                      </div>
                                      <h6
                                        className="fw-bold mb-0 text-dark text-nowrap keyworsdTitle"
                                        style={{ fontSize: "13px" }}
                                      >
                                        {card.title}
                                      </h6>
                                    </div>
                                    <ul className="list-unstyled d-flex flex-column gap-2 mb-0">
                                      {card.items.map((item, i) => (
                                        <li
                                          key={i}
                                          className="d-flex align-items-start gap-2"
                                        >
                                          <span
                                            className="flex-shrink-0 text-dark fw-bold"
                                            style={{
                                              marginTop: "-2px",
                                              fontSize: "18px",
                                            }}
                                          >
                                            •
                                          </span>
                                          <span className="text-secondary keyworsdText">
                                            {item}
                                          </span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                ))}
                              </div>

                              {/* Mobile/Tablet View: Carousel */}
                              <div className="d-lg-none">
                                <InsightsCarousel cards={insightCards} />
                              </div>
                            </div>
                          );
                        })()}
                      </div>

                      {/* Market Segmentation Card */}
                      <div
                        className="card border-0 shadow-sm mb-4"
                        id="segmentation-analysis"
                      >
                        <div className="hero-gradient-bg p-3">
                          <h4 className="fw-bold mb-0 reports-hero-title">
                            Market Segmentation Analysis
                          </h4>
                        </div>
                        <div className="card-body p-3">
                          {(report.SegmentationAnalysis || []).map((seg, idx) =>
                            renderSegmentationContent(seg, idx),
                          )}
                        </div>
                      </div>

                      {/* IP Overview Card */}
                      <div
                        className="card border-0 shadow-sm bg-white mb-4"
                        id="ip-overview"
                      >
                        <div className="hero-gradient-bg p-3">
                          <h4 className="fw-bold mb-0 reports-hero-title">
                            IP Overview
                          </h4>
                        </div>
                        <div className="p-3">
                          {report.ipOverview ? (
                            <div
                              className="dynamic-content"
                              dangerouslySetInnerHTML={{
                                __html: report.ipOverview,
                              }}
                            />
                          ) : (
                            <>
                              <h6 className="fw-bold">
                                Surge in Patent Filings
                              </h6>
                              <div className="text-center my-3">
                                <img
                                  src={ipOverviewImg}
                                  alt="IP Overview"
                                  className="img-fluid"
                                />
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Recent Developments Card */}
                      <div
                        className="card border-0 shadow-sm bg-white mb-4"
                        id="recent-developments"
                      >
                        <div className="hero-gradient-bg p-3">
                          <h4 className="fw-bold mb-0 reports-hero-title">
                            Recent Developments
                          </h4>
                        </div>
                        <ul className="list-unstyled d-flex flex-column gap-2 mb-0 p-3">
                          {(Array.isArray(report.RecentDevelopments)
                            ? report.RecentDevelopments
                            : typeof report.RecentDevelopments === "string"
                              ? report.RecentDevelopments.split(/\r?\n/)
                              : []
                          )
                            .filter((item) => item?.trim())
                            .map((item, i) => (
                              <li
                                key={i}
                                className="d-flex align-items-start gap-2 text-secondary"
                              >
                                <span
                                  className="flex-shrink-0"
                                  style={{
                                    marginTop: "-2px",
                                    fontSize: "18px",
                                  }}
                                >
                                  •
                                </span>
                                <span>{item.trim()}</span>
                              </li>
                            ))}
                        </ul>
                      </div>

                      {/* Competitive Landscape Card */}
                      <div
                        className="card border-0 shadow-sm bg-white mb-4"
                        id="competitive-landscape"
                      >
                        <div className="hero-gradient-bg p-3">
                          <h4 className="fw-bold mb-0 reports-hero-title">
                            Competitive Landscape
                          </h4>
                        </div>
                        <div className="p-3 text-secondary">
                          {report.CompetitorAnalysis ? (
                            <div
                              className="dynamic-content Competitive-Landscape"
                              dangerouslySetInnerHTML={{
                                __html: report.CompetitorAnalysis.replace(
                                  /<table/gi,
                                  '<div class="table-responsive"><table',
                                )
                                  .replace(/<\/table>/gi, "</table></div>")
                                  .replace(
                                    /<p[^>]*>\s*\[Type here\]\s*<\/p>/gi,
                                    "",
                                  )
                                  .replace(/\[Type here\]/gi, ""),
                              }}
                            />
                          ) : (
                            <img
                              src={competitiveImg}
                              alt="Competitive Landscape"
                              className="img-fluid rounded shadow-sm"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div id="market-scope" className="mb-4">
                    <div className="card border-0 shadow-sm bg-white">
                      <div className="hero-gradient-bg p-3">
                        <h4 className="fw-bold mb-0 reports-hero-title">
                          Market Report Scope
                        </h4>
                      </div>
                      <div className="p-3">
                        <div className="table-responsive scope-table-wrapper">
                          <table className="scope-table table-bordered">
                            <thead>
                              <tr>
                                <th className="scope-table-header">
                                  Report Features
                                </th>
                                <th className="scope-table-header">
                                  Description
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {/* Sequence: Historical Data for, Base Year, Market Size, Forecast Period, Forecast Period CAGR, Largest Market, Fastest Growing, Report Pages */}
                              <tr>
                                <td className="label-cell">
                                  Historical Data for:
                                </td>
                                <td className="value-cell">
                                  {report.ReportYear
                                    ? `${report.ReportYear - 4} To ${report.ReportYear - 1}`
                                    : "2020 To 2024"}
                                </td>
                              </tr>
                              <tr>
                                <td className="label-cell">Base Year:</td>
                                <td className="value-cell">
                                  {report.ReportYear || "2024"}
                                </td>
                              </tr>
                              <tr>
                                <td className="label-cell">
                                  Market Size ({report.ReportYear || "2026"}):
                                </td>
                                <td className="value-cell">
                                  {report.MarketSize}
                                </td>
                              </tr>
                              <tr>
                                <td className="label-cell">Forecast Period:</td>
                                <td className="value-cell">
                                  {report.StudyPeriod}
                                </td>
                              </tr>
                              <tr>
                                <td className="label-cell">
                                  Forecast Period CAGR:
                                </td>
                                <td className="value-cell">{report.CAGR}</td>
                              </tr>
                              <tr>
                                <td className="label-cell">Largest Market:</td>
                                <td className="value-cell">
                                  {report.LargestMarket || "N/A"}
                                </td>
                              </tr>
                              <tr>
                                <td className="label-cell">Fastest Growing:</td>
                                <td className="value-cell">
                                  {report.FastestGrowingSegment
                                    ? report.FastestGrowingParent
                                      ? `${report.FastestGrowingSegment} (${report.FastestGrowingParent})`
                                      : report.FastestGrowingSegment
                                    : "N/A"}
                                </td>
                              </tr>
                              <tr>
                                <td className="label-cell">Report Pages:</td>
                                <td className="value-cell">
                                  {report.Pages || "N/A"}
                                </td>
                              </tr>

                              {/* Full Width Sections */}
                              {[
                                {
                                  label: "Geographies covered:",
                                  value: report.RegionsCovered,
                                },
                                {
                                  label: "Segments covered:",
                                  value:
                                    report.SegmentationAnalysis &&
                                    report.SegmentationAnalysis.length > 0
                                      ? report.SegmentationAnalysis.map(
                                          (seg) => seg.title,
                                        ).join(", ")
                                      : null,
                                },
                                {
                                  label: "Companies covered:",
                                  value: report.MajorPlayers,
                                  isList: true,
                                  splitOn: ".",
                                },
                                {
                                  label: "Growth Drivers:",
                                  value: report.MarketDynamics?.drivers,
                                  isList: true,
                                  maxItems: 3,
                                },
                                {
                                  label: "Restraints & Challenges:",
                                  value: report.MarketDynamics?.restraints,
                                  isList: true,
                                  maxItems: 3,
                                },
                              ]
                                .filter((row) => row.value)
                                .map((row, idx) => (
                                  <tr key={`full-${idx}`}>
                                    <td className="label-cell">{row.label}</td>
                                    <td className="value-cell">
                                      {row.isList ? (
                                        <ul className="mb-0 ps-3">
                                          {(() => {
                                            let items = Array.isArray(row.value)
                                              ? row.value.length === 1 &&
                                                typeof row.value[0] === "string"
                                                ? row.value[0].split(
                                                    /,\s*(?=[A-Z])/,
                                                  )
                                                : row.value
                                              : typeof row.value === "string"
                                                ? row.value.includes("\n")
                                                  ? row.value.split("\n")
                                                  : row.value.split(
                                                      /,\s*(?=[A-Z])/,
                                                    )
                                                : [];
                                            items = items.filter((item) =>
                                              item?.trim(),
                                            );

                                            const renderItem = (item, idx) => {
                                              // Extract the first complete sentence (up to the first period)
                                              let text = item.trim();
                                              const firstPeriodIdx =
                                                text.indexOf(".");
                                              if (firstPeriodIdx !== -1) {
                                                text = text.substring(
                                                  0,
                                                  firstPeriodIdx + 1,
                                                );
                                              }

                                              return (
                                                <li key={idx} className="mb-1">
                                                  <div
                                                    style={{
                                                      lineHeight: "1.4",
                                                    }}
                                                  >
                                                    {text}
                                                  </div>
                                                </li>
                                              );
                                            };

                                            if (
                                              row.maxItems &&
                                              items.length > row.maxItems
                                            ) {
                                              return (
                                                <>
                                                  {items
                                                    .slice(0, row.maxItems)
                                                    .map((item, i) =>
                                                      renderItem(item, i),
                                                    )}
                                                </>
                                              );
                                            }

                                            return items.map((item, i) =>
                                              renderItem(item, i),
                                            );
                                          })()}
                                        </ul>
                                      ) : (
                                        row.value
                                      )}
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Table of Contents Section */}
                  <div id="table-of-contents" className="mb-4">
                    <div className="card border-0 shadow-sm bg-white">
                      <div className="hero-gradient-bg p-3">
                        <h4 className="fw-bold mb-0 reports-hero-title">
                          Table of Contents
                        </h4>
                      </div>
                      <div className="p-3">
                        <TocAccordion
                          html={report.TableOfContents?.replace(
                            /<p[^>]*>\s*\[Type here\]\s*<\/p>/gi,
                            "",
                          )
                            ?.replace(/\[Type here\]/gi, "")
                            ?.replace(
                              /<h2[^>]*>\s*Methodology\s*<\/h2>(\s*<div[^>]*><\/div>)?/gi,
                              "",
                            )}
                        />
                      </div>
                    </div>
                  </div>

                  <div id="research-methodology">
                    <div className="card border-0 shadow-sm bg-white">
                      <div className="hero-gradient-bg mb-3 p-3">
                        <h4
                          className="fw-bold mb-0"
                          style={{ fontSize: "1.1rem" }}
                        >
                          Research Methodology
                        </h4>
                      </div>
                      <div className="p-3">
                        <h5 className="fw-bold mb-4 commonColor">
                          We offer diverse range of market reports with our
                          robust methodology
                        </h5>

                        <MethodologyCarousel
                          images={[
                            ResearchMethodology1,
                            ResearchMethodology2,
                            ResearchMethodology3,
                            ResearchMethodology4,
                            ResearchMethodology5,
                          ]}
                        />
                      </div>
                    </div>
                  </div>

                  {report.Faqs && report.Faqs.length > 0 && (
                    <div
                      className="card border-0 shadow-sm bg-white mb-4 mb-lg-0 mt-4"
                      id="faq"
                    >
                      <div className="hero-gradient-bg p-3">
                        <h4 className="fw-bold mb-0 reports-hero-title">
                          Frequently Asked Questions
                        </h4>
                      </div>
                      <div className="p-3">
                        {report.Faqs.map((faq, index) => (
                          <div
                            key={index}
                            className="card border-0 shadow-sm mb-3 FAQ-card"
                          >
                            <div
                              className="card-header bg-white border-0 px-3 d-flex justify-content-between align-items-center cursor-pointer"
                              onClick={() =>
                                setOpenFaqIndex(
                                  openFaqIndex === index ? -1 : index,
                                )
                              }
                            >
                              <h6 className="fw-bold text-dark mb-0">
                                {faq.question || faq.q}
                              </h6>
                              {openFaqIndex === index ? (
                                <IoChevronUp />
                              ) : (
                                <IoChevronDown />
                              )}
                            </div>
                            {openFaqIndex === index && (
                              <div className="card-body pt-0 text-secondary">
                                {faq.answer || faq.a}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Sidebar: Sticky Profile & Table of Contents */}
              <div className="col-lg-2 col-md-2 mb-4 mb-lg-0 px-lg-0 sidebar-details-container">
                <div className="d-none d-md-block">
                  <ReportDetailsSidebar
                    report={report}
                    onBuyNow={onBuyNow}
                    onDownloadSample={() => handleOpenModal("sample_download")}
                    onRequestCustomization={() =>
                      handleOpenModal("customization")
                    }
                    onAddReport={handleSaveReport}
                    isSaved={isSaved}
                    isSampleRequested={inquiryStatus.sampleRequested}
                    isCustomizationRequested={inquiryStatus.customizationRequested}
                  />
                </div>
                <div className="d-md-none">
                  <WhyChooseReport />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mobileTabcontent d-flex hero-actions-container flex-column gap-2">
          <div className="container">
            <div className="d-flex gap-2 w-100">
              <button
                onClick={handleSaveReport}
                className="btn text-white rounded-1 px-3 py-2 d-flex align-items-center justify-content-center gap-2 flex-fill hero-action-btn"
                style={isSaved ? { backgroundColor: "#198754" } : {}}
              >
                {isSaved ? (
                  <FaCheck className="fs-6" />
                ) : (
                  <FaBookmark className="fs-6" />
                )}
                <span className="btn-label">
                  <span className="d-none d-lg-inline">
                    {isSaved ? "Report Saved" : "Add Report"}
                  </span>
                  <span className="d-lg-none">{isSaved ? "Saved" : "Add"}</span>
                </span>
              </button>
              <button
                onClick={() => handleOpenModal("sample_download")}
                disabled={inquiryStatus.sampleRequested}
                className="btn text-white rounded-1 px-3 py-2 d-flex align-items-center justify-content-center gap-2 flex-fill hero-action-btn"
                style={inquiryStatus.sampleRequested ? { backgroundColor: "#198754", opacity: 0.95, cursor: "not-allowed" } : {}}
                title={inquiryStatus.sampleRequested ? "Sample already requested in the last 7 days" : "Request a Sample"}
              >
                {inquiryStatus.sampleRequested ? (
                  <FaCheck className="fs-6" />
                ) : (
                  <FaEnvelope className="fs-6" />
                )}
                <span className="btn-label">
                  <span className="d-none d-lg-inline">
                    {inquiryStatus.sampleRequested ? "Sample Requested" : "Request a Sample"}
                  </span>
                  <span className="d-lg-none">
                    {inquiryStatus.sampleRequested ? "Requested" : "Sample"}
                  </span>
                </span>
              </button>
              <button
                onClick={() => handleOpenModal("customization")}
                disabled={inquiryStatus.customizationRequested}
                className="btn text-white rounded-1 px-3 py-2 d-flex align-items-center justify-content-center gap-2 flex-fill hero-action-btn"
                style={inquiryStatus.customizationRequested ? { backgroundColor: "#198754", opacity: 0.95, cursor: "not-allowed" } : {}}
                title={inquiryStatus.customizationRequested ? "Customization already requested in the last 7 days" : "Customize Report"}
              >
                {inquiryStatus.customizationRequested ? (
                  <FaCheck className="fs-6" />
                ) : (
                  <FaFileAlt className="fs-6" />
                )}
                <span className="btn-label">
                  <span className="d-none d-lg-inline">
                    {inquiryStatus.customizationRequested ? "Customization Requested" : "Customize Report"}
                  </span>
                  <span className="d-lg-none">
                    {inquiryStatus.customizationRequested ? "Customized" : "Customize"}
                  </span>
                </span>
              </button>
              <button
                onClick={onBuyNow}
                className="btn text-white rounded-1 px-3 py-2 d-flex align-items-center justify-content-center gap-2 flex-fill hero-action-btn"
              >
                <IoCartOutline className="fs-6" />
                <span className="btn-label">
                  <span className="d-none d-lg-inline">Buy Now</span>
                  <span className="d-lg-none">Buy</span>
                </span>
              </button>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

const ReportSkeleton = () => (
  <div className="report-view-page">
    <div
      className="bgcolor mb-4 py-5 position-relative overflow-hidden"
      style={{ minHeight: "400px" }}
    >
      <div className="hero-absolute-bg bg-dark opacity-25" />
      <div
        className="container-fluid px-3 px-lg-5 position-relative"
        style={{ zIndex: 1 }}
      >
        <div className="row">
          <div className="col-lg-8">
            <div
              className="skeleton mb-3"
              style={{ width: "100px", height: "24px" }}
            ></div>
            <div
              className="skeleton mb-3"
              style={{ width: "80%", height: "48px" }}
            ></div>
            <div
              className="skeleton mb-4"
              style={{ width: "100%", height: "80px" }}
            ></div>
            <div className="d-flex gap-3 mt-4">
              <div
                className="skeleton"
                style={{ width: "150px", height: "40px" }}
              ></div>
              <div
                className="skeleton"
                style={{ width: "150px", height: "40px" }}
              ></div>
              <div
                className="skeleton"
                style={{ width: "150px", height: "40px" }}
              ></div>
            </div>
          </div>
          <div className="col-lg-4 d-none d-lg-block">
            <div
              className="skeleton"
              style={{ width: "100%", height: "350px" }}
            ></div>
          </div>
        </div>
      </div>
    </div>
    <div className="container-fluid px-3">
      <div className="row">
        <div className="col-lg-3 d-none d-lg-block">
          <div className="skeleton mb-4" style={{ height: "500px" }}></div>
        </div>
        <div className="col-lg-9">
          <div className="skeleton mb-4" style={{ height: "300px" }}></div>
          <div className="skeleton mb-4" style={{ height: "600px" }}></div>
        </div>
      </div>
    </div>
  </div>
);

export default IndiaDataCenter;
